import type { EffectiveModule, ResolvedSite } from '~~/types/tenant'
import { ALL_MODULE_KEYS, type ModuleKey } from '~/types/onboarding'
import { blockedComponentsForModules, blockedPagesForModules } from '~/utils/templates'
import { isBlockAvailable } from '~/composables/useBuilderMeta'
import { useOnboardingStore } from '~/stores/useOnboardingStore'

export function useTenantSite() {
  return useState<ResolvedSite | null>('tenant-site', () => null)
}

export function useOwnerModules() {
  return useState<EffectiveModule[]>('owner-modules', () => [])
}

export function useOwnerSiteId() {
  return useState<string | null>('owner-site-id', () => null)
}

export function enabledModuleKeySet(modules: EffectiveModule[]): Set<string> {
  return new Set(modules.filter((m) => m.enabled).map((m) => m.key))
}

export function useSiteModules() {
  const tenant = useTenantSite()
  const ownerModules = useOwnerModules()
  const ownerSiteId = useOwnerSiteId()
  const onboarding = useOnboardingStore()

  const hasResolvedSite = computed(() => ownerSiteId.value !== null || tenant.value !== null)

  const modules = computed<EffectiveModule[]>(() => {
    if (ownerSiteId.value !== null) return ownerModules.value
    if (tenant.value) return tenant.value.modules ?? []
    return []
  })

  const siteId = computed(() => ownerSiteId.value ?? tenant.value?.id ?? null)

  const enabledKeys = computed(() => {
    if (hasResolvedSite.value) return enabledModuleKeySet(modules.value)
    return new Set(ALL_MODULE_KEYS.filter((key) => onboarding.answers.modules[key]))
  })

  const moduleRecord = computed(() => {
    if (!hasResolvedSite.value) return onboarding.answers.modules
    const record = Object.fromEntries(ALL_MODULE_KEYS.map((k) => [k, false])) as Record<ModuleKey, boolean>
    for (const mod of modules.value) {
      if (mod.enabled && mod.key in record) record[mod.key as ModuleKey] = true
    }
    return record
  })

  const blockedPages = computed(() => blockedPagesForModules(moduleRecord.value))
  const blockedComponents = computed(() => blockedComponentsForModules(moduleRecord.value))

  function allowsBlock(component: string): boolean {
    return isBlockAvailable(component, enabledKeys.value, siteId.value)
  }

  const deprecatedModules = computed(() => modules.value.filter((m) => m.status === 'deprecated'))

  return {
    modules,
    siteId,
    enabledKeys,
    moduleRecord,
    blockedPages,
    blockedComponents,
    allowsBlock,
    deprecatedModules,
  }
}
